import { useRecentActivity } from '@/client/hooks/use-recent-trades';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

import { ActivityLine } from './activity-line';

// Mobile-only activity broadcast. There is no footer on mobile, so the line
// surfaces as a discreet pill floating just above the bottom nav. It shows
// while the user is idle and slides away the moment they scroll/swipe, then
// eases back once scrolling settles -- never competing with an active reader.
const ROTATE_MS = 6000;
const IDLE_MS = 1200;

export const TheActivityToast = () => {
  const trades = useRecentActivity(20);
  const [index, setIndex] = useState(0);
  const [scrolling, setScrolling] = useState(false);
  const idleTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (trades.length < 2) return;
    const id = setInterval(() => setIndex(i => i + 1), ROTATE_MS);
    return () => clearInterval(id);
  }, [trades.length]);

  useEffect(() => {
    const onMove = () => {
      setScrolling(true);
      clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => setScrolling(false), IDLE_MS);
    };
    // scroll catches momentum + container scroll; touchmove hides instantly on
    // the first finger drag, even at the top of the page where scroll may not fire.
    window.addEventListener('scroll', onMove, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    return () => {
      window.removeEventListener('scroll', onMove);
      window.removeEventListener('touchmove', onMove);
      clearTimeout(idleTimer.current);
    };
  }, []);

  if (trades.length === 0) return null;

  const trade = trades[index % trades.length];
  const visible = !scrolling;

  return (
    <div className="pos-fixed inset-x-0 bottom-20 z-20 flex justify-center px-3 pointer-events-none md:hidden">
      <AnimatePresence>
        {visible && (
          <motion.div
            key="activity-toast"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="pointer-events-auto max-w-full overflow-hidden rounded-full border border-border bg-card/95 px-3.5 py-1.5 shadow-lg backdrop-blur"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={trade.uuid}
                initial={{ y: 8, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -8, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <ActivityLine trade={trade} />
              </motion.div>
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

TheActivityToast.displayName = 'TheActivityToast';
